import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MembershipSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  const checkPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      // Use the subscription-aware status endpoint
      const response = await axios.get(`${API_URL}/api/subscriptions/checkout-status/${sessionId}`);
      const data = response.data;

      // For subscription checkouts, success = status:complete + payment_status:paid
      // (Stripe sometimes shows payment_status:'no_payment_required' for trials too — accept both)
      const succeeded =
        data.status === 'complete' &&
        (data.payment_status === 'paid' || data.payment_status === 'no_payment_required');

      if (succeeded) {
        setStatus('success');
        setPaymentDetails(data);
        return;
      }
      if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Still pending — keep polling
      setPollAttempts(attempts + 1);
      setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      // 404 right after Stripe redirect can happen — keep polling a couple times
      if (error.response?.status === 404 && attempts < 3) {
        setPollAttempts(attempts + 1);
        setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
        return;
      }
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId, checkPaymentStatus]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-800 text-center">
          <CardHeader>
            <Loader2 className="w-16 h-16 text-lime-400 animate-spin mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Processing Payment...</CardTitle>
            <CardDescription className="text-zinc-400">
              Please wait while we confirm your payment ({pollAttempts}/5)
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-800 text-center">
          <CardHeader>
            <CheckCircle className="w-16 h-16 text-lime-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Payment Successful! 🎉</CardTitle>
            <CardDescription className="text-zinc-400">
              Welcome to {paymentDetails?.metadata?.package_name || 'your membership'}!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-zinc-800 p-4 rounded-lg">
              <p className="text-sm text-zinc-400">Amount Paid</p>
              <p className="text-2xl font-bold text-lime-400">
                ${(paymentDetails?.amount_total / 100).toFixed(2)}
              </p>
            </div>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/rewards')}
                className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black font-semibold hover:from-lime-600 hover:to-lime-700"
              >
                View Your Rewards
              </Button>
              <Button
                onClick={() => navigate('/booking')}
                variant="outline"
                className="w-full border-purple-500 text-purple-400 hover:bg-purple-500/10"
              >
                Book Your First Appointment
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900 border-zinc-800 text-center">
        <CardHeader>
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <CardTitle className="text-2xl text-white">
            {status === 'timeout' ? 'Payment Check Timeout' : 'Payment Failed'}
          </CardTitle>
          <CardDescription className="text-zinc-400">
            {status === 'timeout'
              ? 'We couldn\'t verify your payment. Please check your email for confirmation or contact support.'
              : 'Something went wrong with your payment. Please try again.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => navigate('/membership')}
            className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-lime-400 font-semibold hover:from-purple-700 hover:to-purple-800"
          >
            Back to Membership
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipSuccess;
